import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { ChevronDown, ChevronRight, Trash2, Folder, FolderOpen, FolderPlus, Plus, X } from 'lucide-react';
import { isUiEndpoint } from 'framework/access/UiRegistry';
import { FeatureGroupNode as FeatureGroupNodeType, Feature } from 'types/apis/rbacInterfaces';

const chipSx = { height: 18, fontSize: 11, color: 'text.secondary' } as const;

interface FeatureGroupNodeProps {
  node: FeatureGroupNodeType;
  depth: number;
  expanded: Set<number>;
  /** True while a search is active — every surviving node renders open. */
  forceExpand?: boolean;
  /** Hides all mutation affordances (System/Blueprint groups). */
  readOnly?: boolean;
  onToggleExpand: (id: number) => void;
  onDelete: (node: FeatureGroupNodeType) => void;
  onAddFeatures: (node: FeatureGroupNodeType) => void;
  onAddSubgroup: (node: FeatureGroupNodeType) => void;
  onRemoveFeature: (feature: Feature, node: FeatureGroupNodeType) => void;
}

export const FeatureGroupNode = ({
  node,
  depth,
  expanded,
  forceExpand = false,
  readOnly = false,
  onToggleExpand,
  onDelete,
  onAddFeatures,
  onAddSubgroup,
  onRemoveFeature,
}: FeatureGroupNodeProps) => {
  const featureCount = node.features?.length || 0;
  const childCount = node.children?.length || 0;
  const hasChildren = childCount > 0 || featureCount > 0;
  const isExpanded = forceExpand || expanded.has(node.id);

  const countsLabel = [
    childCount > 0 ? `${childCount} subgroup${childCount > 1 ? 's' : ''}` : '',
    featureCount > 0 ? `${featureCount} permission${featureCount > 1 ? 's' : ''}` : '',
  ].filter(Boolean).join(' · ');

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          pl: depth * 2,
          pr: 0.5,
          py: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
          '&:hover .rbac-node-actions': { opacity: 1 },
        }}
      >
        <IconButton
          size="small"
          onClick={() => onToggleExpand(node.id)}
          disabled={!hasChildren || forceExpand}
          sx={{ p: 0.25, visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </IconButton>
        <Box component="span" sx={{ display: 'inline-flex', color: 'primary.main', flexShrink: 0 }}>
          {isExpanded && hasChildren ? <FolderOpen size={16} /> : <Folder size={16} />}
        </Box>
        <Tooltip title={node.description || ''} placement="top-start">
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {node.name}
          </Typography>
        </Tooltip>
        {countsLabel && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
            {countsLabel}
          </Typography>
        )}
        {!readOnly && (
          <Box className="rbac-node-actions" sx={{ opacity: 0, transition: 'opacity 0.15s', display: 'flex', ml: 'auto' }}>
            <Tooltip title="Add permissions to this group">
              <IconButton size="small" onClick={() => onAddFeatures(node)}>
                <Plus size={14} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add subgroup">
              <IconButton size="small" onClick={() => onAddSubgroup(node)}>
                <FolderPlus size={14} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete group">
              <IconButton size="small" onClick={() => onDelete(node)}>
                <Trash2 size={14} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {isExpanded && (
        <Box>
          {node.children?.map((child) => (
            <FeatureGroupNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              forceExpand={forceExpand}
              readOnly={readOnly}
              onToggleExpand={onToggleExpand}
              onDelete={onDelete}
              onAddFeatures={onAddFeatures}
              onAddSubgroup={onAddSubgroup}
              onRemoveFeature={onRemoveFeature}
            />
          ))}
          {node.features?.map((feature) => (
            <Box
              key={feature.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                pl: (depth + 1) * 2 + 4,
                pr: 0.5,
                py: 0.375,
                borderRadius: 1,
                '&:hover': { bgcolor: 'action.hover' },
                '&:hover .rbac-feature-actions': { opacity: 1 },
              }}
            >
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {feature.label || feature.endpoint}
              </Typography>
              {feature.label && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: 'monospace' }}>
                  {feature.endpoint}
                </Typography>
              )}
              {isUiEndpoint(feature.endpoint) && (
                <Chip size="small" variant="outlined" label="UI" sx={chipSx} />
              )}
              {feature.auto_discovered && (
                <Chip size="small" variant="outlined" label="auto" sx={chipSx} />
              )}
              {!readOnly && (
                <Box className="rbac-feature-actions" sx={{ opacity: 0, transition: 'opacity 0.15s', ml: 'auto' }}>
                  <Tooltip title="Remove from group (permission stays in catalog)">
                    <IconButton size="small" onClick={() => onRemoveFeature(feature, node)}>
                      <X size={12} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          ))}
          {!hasChildren && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: (depth + 1) * 2 + 4, py: 0.25, display: 'block' }}>
              {readOnly ? 'Empty group.' : 'Empty group — use + to add permissions.'}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
